import { useNavigate, useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Pagination from '@mui/material/Pagination';
import Skeleton from '@mui/material/Skeleton';
import Tooltip from '@mui/material/Tooltip';
import Switch from '@mui/material/Switch';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

import { TCoupon, ORDER_TYPE_LABELS } from '../schemas';

type Props = {
  coupons: TCoupon[];
  totalCount: number;
  page: number;
  limit: number;
  onPageChange: (page: number) => void;
  isLoading?: boolean;
  onDelete: (id: string) => void;
  onToggleStatus: (id: string) => void;
  togglingCouponId?: string | null;
};

const CouponList = ({
  coupons,
  totalCount,
  page,
  limit,
  onPageChange,
  isLoading = false,
  onDelete,
  onToggleStatus,
  togglingCouponId,
}: Props) => {
  const navigate = useNavigate();
  const { rest_id: restaurantId } = useParams<{ rest_id: string }>();
  const totalPages = Math.ceil(totalCount / limit);

  const handleAdd = () => {
    navigate(`/restaurant/${restaurantId}/coupons/new`);
  };

  const handleEdit = (couponId: string) => {
    navigate(`/restaurant/${restaurantId}/coupons/edit/${couponId}`);
  };

  const formatDiscount = (coupon: TCoupon) => {
    if (coupon.tiers && coupon.tiers.length > 0) {
      return `${coupon.tiers.length} tier${coupon.tiers.length > 1 ? 's' : ''}`;
    }
    if (coupon.discountType === 'percentage') return `${coupon.discountPercentage}%`;
    return `${coupon.discountAmount?.currency || 'GBP'} ${coupon.discountAmount?.value || 0}`;
  };

  const formatDateRange = (start?: string | null, end?: string | null) => {
    if (!start && !end) return '—';
    const startStr = start ? new Date(start).toLocaleDateString() : '∞';
    const endStr = end ? new Date(end).toLocaleDateString() : '∞';
    return `${startStr} – ${endStr}`;
  };

  const formatOrderTypes = (types: number[]) => {
    if (!types || types.length === 0) return '—';
    return types.map((t) => ORDER_TYPE_LABELS[t] || t).join(', ');
  };

  return (
    <Box>
      <Box sx={styles.header}>
        <Typography sx={styles.count}>
          {!isLoading && `${totalCount} coupon${totalCount !== 1 ? 's' : ''}`}
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleAdd} sx={styles.addButton}>
          Add Coupon
        </Button>
      </Box>

      {isLoading ? (
        <Box>
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} variant="rectangular" height={52} sx={{ mb: 0.5, borderRadius: 1 }} />
          ))}
        </Box>
      ) : coupons.length === 0 ? (
        <Box sx={styles.emptyContainer}>
          <Typography sx={styles.emptyText}>No coupons found. Add your first coupon.</Typography>
        </Box>
      ) : (
        <TableContainer sx={styles.tableContainer}>
          <Table size="small">
            <TableHead>
              <TableRow sx={styles.headerRow}>
                <TableCell sx={styles.headerCell}>Code</TableCell>
                <TableCell sx={styles.headerCell}>Discount</TableCell>
                <TableCell sx={styles.headerCell}>Order Types</TableCell>
                <TableCell sx={styles.headerCell}>Valid Period</TableCell>
                <TableCell sx={styles.headerCell}>Usage</TableCell>
                <TableCell sx={styles.headerCell}>Status</TableCell>
                <TableCell sx={styles.headerCell} align="right">
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {coupons.map((coupon) => (
                <TableRow key={coupon._id} sx={styles.row}>
                  <TableCell>
                    <Typography sx={styles.code}>{coupon.code}</Typography>
                    {coupon.description && (
                      <Typography sx={styles.description}>{coupon.description}</Typography>
                    )}
                  </TableCell>

                  <TableCell>
                    <Typography sx={styles.cellText}>{formatDiscount(coupon)}</Typography>
                  </TableCell>

                  <TableCell>
                    <Typography sx={styles.cellText}>
                      {formatOrderTypes(coupon.applicableOrderType)}
                    </Typography>
                  </TableCell>

                  <TableCell>
                    <Typography sx={styles.cellText}>
                      {formatDateRange(coupon.validFrom, coupon.validTo)}
                    </Typography>
                  </TableCell>

                  <TableCell>
                    <Typography sx={styles.cellText}>
                      {coupon.usageCount ?? 0}
                      {coupon.usageLimit ? ` / ${coupon.usageLimit}` : ''}
                    </Typography>
                  </TableCell>

                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Tooltip title={`Click to ${coupon.status === 'active' ? 'disable' : 'activate'}`} arrow>
                        <Box sx={togglingCouponId === coupon._id ? styles.blinking : {}}>
                          <Switch
                            checked={coupon.status === 'active'}
                            onChange={() => onToggleStatus(coupon._id)}
                            disabled={togglingCouponId === coupon._id}
                            size="small"
                            sx={styles.switch}
                          />
                        </Box>
                      </Tooltip>
                      <Chip
                        label={coupon.status === 'active' ? 'Active' : 'Disabled'}
                        size="small"
                        sx={coupon.status === 'active' ? styles.activeChip : styles.inactiveChip}
                      />
                    </Box>
                  </TableCell>

                  <TableCell align="right">
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => handleEdit(coupon._id)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" onClick={() => onDelete(coupon._id)} color="error">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {totalPages > 1 && (
        <Box sx={styles.paginationContainer}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, newPage) => onPageChange(newPage)}
            color="primary"
            shape="rounded"
            showFirstButton
            showLastButton
          />
        </Box>
      )}
    </Box>
  );
};

export default CouponList;

const styles = {
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    mb: 2,
  },
  count: {
    fontFamily: 'GilroyRegular',
    fontSize: 14,
    color: '#666',
  },
  addButton: {
    backgroundColor: '#1a1a1a',
    fontFamily: 'GilroySemiBold',
    fontSize: 13,
    textTransform: 'none' as const,
    '&:hover': { backgroundColor: '#333' },
  },
  tableContainer: {
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    overflow: 'hidden',
  },
  headerRow: {
    backgroundColor: '#f5f5f5',
  },
  headerCell: {
    fontFamily: 'GilroySemiBold',
    fontSize: 13,
    color: '#555',
  },
  row: {
    '&:last-child td': { border: 0 },
    '&:hover': { backgroundColor: '#fafafa' },
  },
  code: {
    fontFamily: 'GilroySemiBold',
    fontSize: 14,
    color: '#1a1a1a',
    letterSpacing: 0.5,
  },
  description: {
    fontFamily: 'GilroyRegular',
    fontSize: 12,
    color: '#888',
  },
  cellText: {
    fontFamily: 'GilroyRegular',
    fontSize: 13,
    color: '#333',
  },
  activeChip: {
    backgroundColor: '#e8f5e9',
    color: '#2e7d32',
    fontFamily: 'GilroySemiBold',
    fontSize: 11,
  },
  inactiveChip: {
    backgroundColor: '#fce4ec',
    color: '#c62828',
    fontFamily: 'GilroySemiBold',
    fontSize: 11,
  },
  switch: {
    '& .MuiSwitch-switchBase.Mui-checked': {
      color: '#2e7d32',
    },
    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
      backgroundColor: '#4caf50',
    },
  },
  blinking: {
    '@keyframes blink': {
      '0%': { opacity: 1 },
      '50%': { opacity: 0.4 },
      '100%': { opacity: 1 },
    },
    animation: 'blink 1s ease-in-out infinite',
  },
  emptyContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '2rem',
    backgroundColor: '#f5f5f5',
    borderRadius: '8px',
    minHeight: '150px',
  },
  emptyText: {
    fontFamily: 'GilroyRegular',
    fontSize: 14,
    color: '#666',
  },
  paginationContainer: {
    display: 'flex',
    justifyContent: 'center',
    mt: 2,
  },
};
